import './index.less';

import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { Button, Form, Input, message, Select } from 'antd';
import classNames from 'classnames';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import auditVerifySvg from '@/assets/audit_verify.svg';
import IconArrowFilled from '@/assets/icon-arrow-filled.svg';
import { useUserInfo } from '@/hooks/useUserInfo';
import { useI18nContext } from '@/i18n/I18n';
import { getPorInfo, postMerkleTreeInfo } from '@/services/por';
import { isMobile } from '@/utils/ua';

interface ISubmit {
  auditId?: string;
  merkleLeaf?: string;
}
const AuditVerify: FC = () => {
  const [$t, lang] = useI18nContext();
  const navigate = useNavigate();
  const { msgError, user } = useUserInfo();
  const [form] = Form.useForm();
  const [disable, setDisable] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [auditList, setAuditList] = useState<
    Array<{ auditId: string; snapshotDt: string }>
  >([]);

  useEffect(() => {
    // 请求审计列表数据
    getPorInfo()
      .then((res) => {
        setAuditList(
          res?.result?.items?.map((item) => ({
            auditId: item.auditId,
            snapshotDt: item.snapshotDt,
          })) || [],
        );
        form.setFieldValue('auditId', res?.result?.items?.[0]?.auditId);
      })
      .catch((err: any) => {
        if (err) {
          msgError(err);
        } else {
          message.error('network error! try again later');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const onValuesChange = (changedFields: any, allFields: any) => {
    if (allFields?.auditId?.trim() && allFields?.merkleLeaf?.trim()) {
      setDisable(false);
    } else {
      setDisable(true);
    }
  };

  // 提交
  const onFinish = () => {
    const values = form.getFieldsValue();
    const params: ISubmit = {};
    if (values?.auditId) {
      params.auditId = values?.auditId.trim();
    }
    if (values?.merkleLeaf) {
      params.merkleLeaf = values?.merkleLeaf.trim();
    }
    setLoading(true);
    postMerkleTreeInfo(params)
      .then((res: any) => {
        setLoading(false);
        if (res?.result?.success === true) {
          navigate(
            `/app/user/merkle-tree?auditId=${values.auditId}&leaf=${values.merkleLeaf}`,
          );
        } else {
          message.warning(res?.ret_msg || '');
        }
      })
      .catch((err: any) => {
        setLoading(false);
        if (err) {
          msgError(err);
        } else {
          message.error('network error! try again later');
        }
      });
  };

  useEffect(() => {
    if (user.is_submember) {
      // 母账号时在 /app/user/audit页面切换子账号 禁止子账号看到audit页面
      navigate('/app/user/proof-of-reserve');
    }
  }, [user]);

  // 返回上一页
  const goBack = (e: any) => {
    e && e.preventDefault();
    navigate('/app/user/proof-of-reserve');
  };

  return (
    <div
      className={classNames('audit-verify-container', {
        'audit-verify-container-h5': isMobile(),
      })}
    >
      {isMobile() && (
        <div className="h5-header">
          <Button
            type="link"
            className={classNames('back-btn', {
              'right-position': lang === 'ar-SA',
              'left-position': lang !== 'ar-SA',
            })}
            icon={
              lang === 'ar-SA' ? <ArrowRightOutlined /> : <ArrowLeftOutlined />
            }
            onClick={goBack}
          ></Button>
          <span className="page-title">{$t('verifyMyAccount')}</span>
        </div>
      )}
      <div className="audit-verify-content">
        <div>
          <img src={auditVerifySvg} alt="audit-verify" width="160" />
        </div>
        <div data-paint className="title">
          {$t('verifyMyAccount')}
        </div>
        <div className="desc">{$t('searchToCheckMerkle')}</div>
        <Form
          form={form}
          className="verify-form"
          layout="vertical"
          onFinish={onFinish}
          onValuesChange={onValuesChange}
          autoComplete="off"
        >
          <Form.Item
            name="auditId"
            label={$t('auditId')}
            rules={[
              {
                required: true,
                message: $t('enterAuditId'),
              },
            ]}
          >
            <Select
              placeholder={$t('enterAuditId')}
              bordered={false}
              suffixIcon={
                <img
                  className="icon-arrow-filled"
                  src={IconArrowFilled}
                  alt=""
                />
              }
            >
              {auditList?.map((item) => (
                <Select.Option
                  className="audit-select-option"
                  value={item.auditId}
                  key={item.auditId}
                >
                  {item.auditId}
                </Select.Option>
              ))}
            </Select>
            {/* <Input className="verify-input"  /> */}
          </Form.Item>
          <Form.Item
            name="merkleLeaf"
            label={$t('MERKLE LEAF')}
            rules={[
              {
                required: true,
                message: $t('enterMerkle'),
              },
            ]}
          >
            <Input className="verify-input" placeholder={$t('enterMerkle')} />
          </Form.Item>
          <Form.Item className="submit-item">
            <Button
              className="bybit-btn-primary submit-btn"
              type="primary"
              htmlType="submit"
              disabled={disable}
              loading={loading}
            >
              {$t('Submit')}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default AuditVerify;
